import { z } from "zod";

const PRODUCTION_SUPABASE_HOSTNAME = "azddwmbbocgccymtvkjk.supabase.co";
const PRODUCTION_BACKEND_HOSTNAME = "api.kolaytadilat.com";

const trimString = (value) => (typeof value === "string" ? value.trim() : value);

const appEnvironment = z.preprocess(
  trimString,
  z.enum(["local", "staging", "production"], {
    error: "VITE_APP_ENV is required (local, staging, or production)",
  }),
);

const requiredHttpUrl = (name) => z
  .string({ error: `${name} is required` })
  .trim()
  .min(1, `${name} cannot be empty`)
  .url(`${name} must be a valid URL`)
  .refine((value) => {
    try {
      return ["http:", "https:"].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  }, {
    message: `${name} must use http or https`,
  });

const optionalHttpUrl = (name) => z.preprocess(
  (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
  requiredHttpUrl(name).optional(),
);

const frontendEnvSchema = z
  .object({
    VITE_APP_ENV: appEnvironment,
    VITE_SUPABASE_URL: requiredHttpUrl("VITE_SUPABASE_URL"),
    VITE_SUPABASE_ANON_KEY: z
      .string({ error: "VITE_SUPABASE_ANON_KEY is required" })
      .trim()
      .min(1, "VITE_SUPABASE_ANON_KEY cannot be empty"),
    VITE_BACKEND_URL: requiredHttpUrl("VITE_BACKEND_URL"),
    VITE_SENTRY_DSN: optionalHttpUrl("VITE_SENTRY_DSN"),
  })
  .superRefine((values, ctx) => {
    if (values.VITE_APP_ENV !== "staging") return;

    if (new URL(values.VITE_SUPABASE_URL).hostname.toLowerCase() === PRODUCTION_SUPABASE_HOSTNAME) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["VITE_SUPABASE_URL"],
        message: "A staging application cannot use the known production Supabase project",
      });
    }

    if (new URL(values.VITE_BACKEND_URL).hostname.toLowerCase() === PRODUCTION_BACKEND_HOSTNAME) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["VITE_BACKEND_URL"],
        message: "A staging application cannot use the known production backend API",
      });
    }
  });

function formatIssues(error) {
  return error.issues
    .map((issue) => `  - ${issue.path.join(".") || "<root>"}: ${issue.message}`)
    .join("\n");
}

export function validateFrontendEnv(rawEnv) {
  const result = frontendEnvSchema.safeParse(rawEnv);

  if (!result.success) {
    throw new Error(`[ENV] Invalid frontend configuration:\n${formatIssues(result.error)}`);
  }

  return result.data;
}
